import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const client = axios.create({ baseURL: API, timeout: 60000, withCredentials: true });

// In-flight request dedup for read-heavy GETs that get called by many
// components at once on the same page (facets, categories, category detail).
// Simultaneous calls with identical keys share ONE network round-trip.
// Prevents the request storm we saw on category pages where 4 chip strips
// + 2 filter panels + 1 auto-refresh all hit /api/facets in parallel.
const _inflight = new Map();
function dedupe(key, factory) {
  if (_inflight.has(key)) return _inflight.get(key);
  const p = factory().finally(() => _inflight.delete(key));
  _inflight.set(key, p);
  return p;
}

export const api = {
  settings: () => client.get("/settings").then((r) => r.data),
  categories: (params = {}) =>
    dedupe(
      `categories:${JSON.stringify(params)}`,
      () => client.get("/categories", { params }).then((r) => r.data),
    ),
  category: (slug) =>
    dedupe(`category:${slug}`, () => client.get(`/categories/${slug}`).then((r) => r.data)),
  products: (params = {}) =>
    client.get("/products", { params }).then((r) => r.data),
  product: (slug) => client.get(`/products/${slug}`).then((r) => r.data),
  related: (slug, limit = 8) =>
    client.get(`/products/${slug}/related`, { params: { limit } }).then((r) => r.data),
  styleWith: (slug, limit = 6) =>
    client.get(`/products/${slug}/style-with`, { params: { limit } }).then((r) => r.data),
  productsBySlugs: (slugs) =>
    client.get("/products/by-slugs", { params: { slugs: slugs.join(",") } }).then((r) => r.data),
  search: (q, limit = 10) =>
    client.get("/search", { params: { q, limit } }).then((r) => r.data),
  checkoutWhatsapp: (payload) =>
    client.post("/checkout/whatsapp", payload).then((r) => r.data),
  checkoutCreateOrder: (payload) =>
    client.post("/checkout/create-order", payload).then((r) => r.data),
  checkoutBokapg: (payload) =>
    client.post("/checkout/bokapg", payload).then((r) => r.data),
  bokapgStatus: (txnId) =>
    client.get(`/checkout/bokapg/status/${encodeURIComponent(txnId)}`).then((r) => r.data),
  checkoutUpiDirect: (payload) =>
    client.post("/checkout/upi-direct", payload).then((r) => r.data),
  getUpiDirectOrder: (orderNumber) =>
    client.get(`/checkout/upi-direct/${encodeURIComponent(orderNumber)}`).then((r) => r.data),
  // Admin — merchant UPI accounts
  adminListUpiAccounts: () =>
    client.get("/admin/upi-accounts").then((r) => r.data),
  adminCreateUpiAccount: (payload) =>
    client.post("/admin/upi-accounts", payload).then((r) => r.data),
  adminUpdateUpiAccount: (id, payload) =>
    client.patch(`/admin/upi-accounts/${encodeURIComponent(id)}`, payload).then((r) => r.data),
  adminDeleteUpiAccount: (id) =>
    client.delete(`/admin/upi-accounts/${encodeURIComponent(id)}`).then((r) => r.data),
  saveCheckoutDraft: (payload) =>
    client.post("/checkout/draft", payload).then((r) => r.data),
  facets: (category) =>
    dedupe(
      `facets:${category || ""}`,
      () =>
        client
          .get("/facets", { params: category ? { category } : {} })
          .then((r) => r.data),
    ),
  reviews: (params = {}) =>
    client.get("/reviews", { params }).then((r) => r.data),
  welcomeCoupon: (email) =>
    client.post("/coupons/welcome", { email }).then((r) => r.data),
  // Lightweight lookup used by cart / checkout to preview whether a code is
  // active and its percent-off before we commit to redeeming it.
  validateCoupon: (code) =>
    client.get(`/coupons/validate`, { params: { code } }).then((r) => r.data),
  brands: (limit = 120) =>
    client.get("/brands", { params: { limit } }).then((r) => r.data),
  brand: (slug, params = {}) =>
    client.get(`/brands/${slug}`, { params }).then((r) => r.data),
  smartSearch: (query, opts = {}) => {
    const { limit = 36, page = 1, intent = null } = opts;
    return client
      .post("/search/smart", { query, limit, page, intent })
      .then((r) => r.data);
  },
  shopperChat: (message, session_id = null) =>
    client.post("/shopper/chat", { message, session_id }).then((r) => r.data),
  // Fire-and-forget popularity signal. Sends `atc` on add-to-cart, `wa`
  // when a WhatsApp query opens for the product, and `checkout` when the
  // customer initiates checkout. Errors are swallowed — engagement tracking
  // must never break the shopping flow.
  trackEngagement: (slug, event) => {
    if (!slug || !event) return Promise.resolve();
    return client.post(`/products/${slug}/engagement`, { event }).catch(() => {});
  },
  homepageConfig: () =>
    dedupe("homepage-config", () => client.get("/homepage-config").then((r) => r.data)),
};

export const formatPrice = (price, symbol = "₹") => {
  const n = parseFloat(price);
  if (!price || isNaN(n)) return "";
  return `${symbol}${n.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
};
